/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useRef, useState } from "react";
import { EventContentArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Button, Text, theme } from "@nordcloud/gnui";
import { NoData } from "~/components";
import { ErrorWrap } from "~/components/Error";
import { LoaderWrap } from "~/components/Loader";
import { ROUTES } from "~/routing/routes";
import { useGetResourceSchedule } from "~/views/resources_v2/hooks";
import { PAST_EVENT_OPACITY, VIEW_OPTIONS, DEFAULT_VIEW } from "../constants";
import { CalendarViewMode, ResourceScheduleEvent } from "../types";
import { getActionColor, mapResourceActionsToCalendarEvents } from "../utils";

type Props = {
  resourceId: string;
};

/**
 * Shared calendar component for displaying a resource's action schedule.
 * Uses the same visual style as the main Timeline calendar (pastel blocks,
 * accent borders, readable text colors).
 */
export function ResourceScheduleCalendar({ resourceId }: Props) {
  const history = useHistory();
  const calendarRef = useRef<FullCalendar>(null);
  const [currentView, setCurrentView] =
    useState<CalendarViewMode>(DEFAULT_VIEW);

  const { futureActions, pastActions, loading, error } = useGetResourceSchedule(
    { resourceId }
  );

  const events = mapResourceActionsToCalendarEvents(
    futureActions as Parameters<typeof mapResourceActionsToCalendarEvents>[0],
    pastActions as Parameters<typeof mapResourceActionsToCalendarEvents>[1]
  );

  const [popoverData, setPopoverData] = useState<{
    visible: boolean;
    x: number;
    y: number;
    event: ResourceScheduleEvent | null;
  }>({ visible: false, x: 0, y: 0, event: null });

  const handleEventClick = useCallback(
    (info: { event: { extendedProps: Record<string, unknown> } }) => {
      const planId = info.event.extendedProps.planId as string;
      if (planId) {
        history.push(ROUTES.plans.generateDetails(planId));
      }
    },
    [history]
  );

  const handleEventMouseEnter = useCallback(
    (info: {
      el: HTMLElement;
      event: { extendedProps: Record<string, unknown> };
    }) => {
      const rect = info.el.getBoundingClientRect();
      const props = info.event
        .extendedProps as ResourceScheduleEvent["extendedProps"];
      setPopoverData({
        visible: true,
        x: rect.right + 8,
        y: rect.top,
        event: {
          ...props,
          id: "",
          title: props.actionName,
          start: "",
        } as ResourceScheduleEvent,
      });
    },
    []
  );

  const handleEventMouseLeave = useCallback(() => {
    setPopoverData({ visible: false, x: 0, y: 0, event: null });
  }, []);

  const handleViewChange = (view: CalendarViewMode) => {
    setCurrentView(view);
    calendarRef.current?.getApi()?.changeView(view);
  };

  const handleToday = () => calendarRef.current?.getApi()?.today();
  const handlePrev = () => calendarRef.current?.getApi()?.prev();
  const handleNext = () => calendarRef.current?.getApi()?.next();

  const hasNoData = events.length === 0 && !loading;

  return (
    <div data-testid="resource-schedule-calendar">
      <LoaderWrap loading={loading} inContent>
        <ErrorWrap error={error}>
          {hasNoData ? (
            <NoData
              hasIcon
              message="There are no scheduled actions for this resource."
            />
          ) : (
            <CalendarWrapper>
              <ToolbarRow>
                <NavigationGroup>
                  <Button severity="low" size="sm" onClick={handleToday}>
                    Today
                  </Button>
                  <Button
                    severity="low"
                    size="sm"
                    icon="chevronLeft"
                    onClick={handlePrev}
                  />
                  <Button
                    severity="low"
                    size="sm"
                    icon="chevronRight"
                    onClick={handleNext}
                  />
                </NavigationGroup>
                <ViewSwitcher>
                  {VIEW_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      severity={currentView === option.value ? "medium" : "low"}
                      size="sm"
                      onClick={() => handleViewChange(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </ViewSwitcher>
              </ToolbarRow>
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView={DEFAULT_VIEW}
                events={events}
                headerToolbar={false}
                allDaySlot={false}
                eventClick={handleEventClick}
                eventMouseEnter={handleEventMouseEnter}
                eventMouseLeave={handleEventMouseLeave}
                eventContent={renderResourceEventContent}
                height="auto"
                nowIndicator
                dayMaxEvents={5}
                eventDisplay="block"
                slotEventOverlap={false}
                eventOverlap={false}
              />
              {popoverData.visible && popoverData.event && (
                <PopoverPositioner
                  style={{ left: popoverData.x, top: popoverData.y }}
                >
                  <ResourcePopover>
                    <Text size="sm" weight="bold" mb={0}>
                      {popoverData.event.actionName}
                    </Text>
                    <Text size="xs" color={theme.color.text.text02} mb={0}>
                      {popoverData.event.actionType} •{" "}
                      {popoverData.event.planName}
                    </Text>
                    {popoverData.event.resourceGroupName && (
                      <Text size="xs" color={theme.color.text.text02} mb={0}>
                        Group: {popoverData.event.resourceGroupName}
                      </Text>
                    )}
                    {popoverData.event.isPast && (
                      <Text size="xs" color={theme.color.text.text03} mb={0}>
                        (Past event)
                      </Text>
                    )}
                  </ResourcePopover>
                </PopoverPositioner>
              )}
            </CalendarWrapper>
          )}
        </ErrorWrap>
      </LoaderWrap>
    </div>
  );
}

/**
 * Converts a hex color to a pastel version by mixing with white.
 * Result is ~85% white + 15% original color for a soft background.
 */
function hexToPastel(hex: string): string {
  const clean = hex.replace("#", "");
  if (clean.length < 6) {
    return "#f5f5f5";
  }

  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);

  const pr = Math.round(r * 0.15 + 255 * 0.85);
  const pg = Math.round(g * 0.15 + 255 * 0.85);
  const pb = Math.round(b * 0.15 + 255 * 0.85);

  return `#${pr.toString(16).padStart(2, "0")}${pg
    .toString(16)
    .padStart(2, "0")}${pb.toString(16).padStart(2, "0")}`;
}

/**
 * Ensures a color has enough contrast to be readable as text on a pastel background.
 */
function ensureReadableColor(hex: string): string {
  const clean = hex.replace("#", "");
  if (clean.length < 6) {
    return "#333333";
  }

  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;

  const rLin = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLin = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLin = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  const luminance = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;

  if (luminance > 0.4) {
    const dr = Math.round(r * 255 * 0.6);
    const dg = Math.round(g * 255 * 0.6);
    const db = Math.round(b * 255 * 0.6);
    return `#${dr.toString(16).padStart(2, "0")}${dg
      .toString(16)
      .padStart(2, "0")}${db.toString(16).padStart(2, "0")}`;
  }

  return hex;
}

function renderResourceEventContent(eventInfo: EventContentArg) {
  const { actionName, actionType, isPast } = eventInfo.event
    .extendedProps as ResourceScheduleEvent["extendedProps"];
  const accentColor = getActionColor(actionName);
  const pastelBg = hexToPastel(accentColor);
  const textColor = ensureReadableColor(accentColor);

  return (
    <EventBlock
      $bgColor={pastelBg}
      $accentColor={accentColor}
      $isPast={isPast}
      data-testid={`resource-schedule-event-${eventInfo.event.id}`}
    >
      <EventActionName $textColor={textColor}>{actionName}</EventActionName>
      <EventSubtitle $textColor={textColor}>{actionType}</EventSubtitle>
    </EventBlock>
  );
}

const CalendarWrapper = styled.div`
  position: relative;

  .fc {
    font-family: inherit;
  }

  .fc-timegrid-slot {
    height: 3em;
  }

  .fc-event {
    cursor: pointer;
    border: none;
    border-radius: 6px;
    padding: 0;
    margin-bottom: 2px;
    background: transparent !important;
  }

  .fc-timegrid-event {
    border-radius: 6px;
    overflow: visible;
    background: transparent !important;
    border: none !important;
  }

  .fc-timegrid-event-harness {
    overflow: visible;
  }

  .fc-timegrid-event:hover {
    z-index: 100 !important;
    box-shadow: ${theme.shadow.shadow04};
  }

  .fc-day-today {
    background-color: #f7f7f8 !important;
  }

  .fc-timegrid-col.fc-day-today {
    box-shadow: inset 0 0 0 1px #e0e0e4;
  }

  .fc-col-header-cell {
    padding: ${theme.spacing.spacing02};
    font-weight: 500;
  }

  .fc-col-header-cell.fc-day-today {
    font-weight: 700;
    color: ${theme.color.interactive.primary};
    background-color: ${theme.color.interactive.primary}14;
    position: relative;
  }

  .fc-col-header-cell.fc-day-today::after {
    content: "";
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${theme.color.interactive.primary};
    margin: 2px auto 0;
  }

  .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
    background-color: ${theme.color.interactive.primary};
    color: #ffffff;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.spacing04};
`;

const NavigationGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.spacing02};
`;

const ViewSwitcher = styled.div`
  display: flex;
  gap: ${theme.spacing.spacing01};
`;

const EventBlock = styled.div<{
  $bgColor: string;
  $accentColor: string;
  $isPast: boolean;
}>`
  background-color: ${({ $bgColor }) => $bgColor};
  border: 1.5px solid ${({ $accentColor }) => $accentColor};
  border-left: 4px solid ${({ $accentColor }) => $accentColor};
  border-radius: 6px;
  padding: 4px 8px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  opacity: ${({ $isPast }) => ($isPast ? PAST_EVENT_OPACITY : 1)};
`;

const EventActionName = styled.div<{ $textColor: string }>`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $textColor }) => $textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const EventSubtitle = styled.div<{ $textColor: string }>`
  font-size: 9px;
  font-weight: 400;
  color: ${({ $textColor }) => $textColor};
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const PopoverPositioner = styled.div`
  position: fixed;
  z-index: 1000;
  pointer-events: none;
`;

const ResourcePopover = styled.div`
  background: ${theme.color.background.ui01};
  border: 1px solid ${theme.color.border.border01};
  border-radius: ${theme.radius.md};
  padding: ${theme.spacing.spacing03};
  box-shadow: ${theme.shadow.shadow04};
  min-width: 180px;
`;
